// Learns the city a user most likely lives in from what they do in the app
// (events they open or buy, cities they filter or search). Stored on-device,
// every call is synchronous and tiny so it never slows the UI.

export type CitySignal = "purchase" | "view" | "filter" | "search" | "ip";

export const SIGNAL_WEIGHTS: Record<CitySignal, number> = {
  purchase: 10,
  view: 2,
  filter: 3,
  search: 3,
  ip: 1,
};

export const HALF_LIFE_DAYS = 30;
export const MIN_SCORE = 5;
const STORAGE_KEY = "nightspy_city_signals";
const DAY_MS = 86_400_000;

type Entry = { score: number; updatedAt: number; country?: string | null };
export type CityScores = Record<string, Entry>;

const normalize = (city: string) => city.trim().replace(/\s+/g, " ");
const keyOf = (city: string) => normalize(city).toLowerCase();

export const decay = (score: number, fromMs: number, toMs: number) =>
  score * Math.pow(0.5, Math.max(0, toMs - fromMs) / (HALF_LIFE_DAYS * DAY_MS));

export const addSignal = (
  scores: CityScores,
  city: string,
  signal: CitySignal,
  now = Date.now(),
  country?: string | null,
): CityScores => {
  if (!city || !normalize(city)) return scores;
  const key = keyOf(city);
  const prev = scores[key];
  const base = prev ? decay(prev.score, prev.updatedAt, now) : 0;
  return {
    ...scores,
    [key]: {
      score: base + SIGNAL_WEIGHTS[signal],
      updatedAt: now,
      country: country ?? prev?.country ?? null,
    },
  };
};

// Returns the display name (original casing is kept in `names`) of the top city.
export const pickCity = (
  scores: CityScores,
  now = Date.now(),
): { key: string; score: number; country: string | null } | null => {
  let best: { key: string; score: number; country: string | null } | null = null;
  for (const [key, e] of Object.entries(scores)) {
    const s = decay(e.score, e.updatedAt, now);
    if (!best || s > best.score) best = { key, score: s, country: e.country ?? null };
  }
  return best && best.score >= MIN_SCORE ? best : null;
};

const titleCase = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());

const read = (): CityScores => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CityScores) : {};
  } catch {
    return {};
  }
};

export const recordCitySignal = (city: string | null | undefined, signal: CitySignal, country?: string | null) => {
  if (!city) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(addSignal(read(), city, signal, Date.now(), country)));
  } catch {
    // storage unavailable; skip silently
  }
};

// Merge extra one-off signals (e.g. purchases fetched from the database) without persisting them.
export const getInferredCity = (
  extra: Array<{ city: string; signal: CitySignal; at?: number; country?: string | null }> = [],
): { city: string; country: string | null } | null => {
  const now = Date.now();
  let scores = read();
  for (const x of extra) {
    const at = x.at ?? now;
    const key = keyOf(x.city);
    const prev = scores[key];
    const added = decay(SIGNAL_WEIGHTS[x.signal], at, now);
    const base = prev ? decay(prev.score, prev.updatedAt, now) : 0;
    scores = { ...scores, [key]: { score: base + added, updatedAt: now, country: x.country ?? prev?.country ?? null } };
  }
  const top = pickCity(scores, now);
  return top ? { city: titleCase(top.key), country: top.country } : null;
};
